/**
 * Essentials feed — chip filtering and "keep wandering".
 *
 * Quick-add is not handled here: each button is a real product form inside
 * Horizon's product-form-component, so the cart drawer, error states and
 * supplier grouping all behave exactly as they do elsewhere. This file only
 * does the two things Horizon has no equivalent for.
 *
 * Filtering is client-side by design. The category axis lives in `category:`
 * tags, and tag filtering is not enabled in Search & Discovery on this store —
 * a ?filter.p.tag= link returns an empty result rather than a filtered one. So
 * chips hide tiles that are already on the page, and the count line follows.
 */
class BunchberryEssentials {
  constructor(root) {
    this.root = root;
    this.feed = root.querySelector('[data-bb-feed]');
    this.chips = [...root.querySelectorAll('[data-bb-chip]')];
    this.more = root.querySelector('[data-bb-more]');
    this.count = root.querySelector('[data-bb-count]');
    if (!this.feed) return;

    this.template = this.count?.dataset.template ?? '';
    this.total = this.count?.dataset.total ?? '';
    this.filter = 'all';

    this.chips.forEach((chip) =>
      chip.addEventListener('click', () => this.#pick(chip.dataset.bbChip))
    );
    this.more?.addEventListener('click', (e) => {
      e.preventDefault();
      this.#loadMore();
    });
  }

  #tiles() {
    return [...this.feed.querySelectorAll('.bb-ess__tile')];
  }

  #pick(value) {
    this.filter = value;
    this.chips.forEach((chip) => {
      const on = chip.dataset.bbChip === value;
      chip.classList.toggle('is-active', on);
      chip.setAttribute('aria-pressed', String(on));
    });
    this.#apply();
  }

  #apply() {
    let shown = 0;
    for (const tile of this.#tiles()) {
      const match = this.filter === 'all' || tile.dataset.bbCat === this.filter;
      tile.hidden = !match;
      if (match) shown++;
    }
    if (this.count && this.template) {
      this.count.textContent = this.template
        .replace('[count]', shown)
        .replace('[total]', this.total);
    }
    return shown;
  }

  /**
   * Fetch the next page and append its tiles. The editorial tiles come with
   * them, because they are woven into the same loop server-side.
   */
  async #loadMore() {
    const url = this.more.getAttribute('href');
    if (!url || this.loading) return;
    this.loading = true;
    const label = this.more.textContent;
    this.more.textContent = this.more.dataset.loading ?? 'Loading…';

    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(String(res.status));
      const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
      const incoming = doc.querySelector('[data-bb-feed]');
      const next = doc.querySelector('[data-bb-more]');

      if (incoming) this.feed.append(...incoming.children);

      // No further page: the button has nothing left to do, so it goes.
      if (next) this.more.setAttribute('href', next.getAttribute('href'));
      else this.more.remove();

      this.#apply();
    } catch {
      // Leave the button in place so the reader can try again, and let the
      // link fall through to a normal page load on a second click.
      this.more.removeEventListener('click', this.#loadMore);
    } finally {
      this.loading = false;
      if (this.more?.isConnected) this.more.textContent = label;
    }
  }
}

for (const root of document.querySelectorAll('.bb-ess')) {
  new BunchberryEssentials(root);
}
